import React from 'react'
import { BaseView, BaseText } from '@/components/base'
import { ContactItem } from '@/components/ContactItem'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { SkeletonListTemplate } from '@/components/SkeletonListTemplate'
import { useLoadUsersAndDeviceContacts } from '@/hooks/useContacts'
import type { UsersAndContacts } from '@/types/contacts'

interface ContactsListProps {
  search?: string
  onContactSelected: (contact: UsersAndContacts) => void
  isEnabledTransferToNonUsers?: boolean
  transferType?: 'send' | 'request'
  giftMessage?: string
  className?: string
  ListHeaderComponent?: React.ReactNode
  showInlineResults?: boolean // Новый пропс для отображения результатов поиска внутри блока Contacts
  isMultiSelectMode?: boolean
  isContactSelected?: (contactId: string) => boolean
}

export const ContactsList: React.FC<ContactsListProps> = ({
  search = '',
  onContactSelected,
  isEnabledTransferToNonUsers = true,
  giftMessage,
  className = '',
  ListHeaderComponent,
  showInlineResults = false,
  isMultiSelectMode = false,
  isContactSelected,
}) => {
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = React.useState(false)

  const {
    searchedUsers,
    isLoadingSearchedUsers,
    isRefreshingUsersContacts,
    onRefreshUsersContacts,
  } = useLoadUsersAndDeviceContacts({ search })

  // Отслеживаем когда данные впервые загрузились
  React.useEffect(() => {
    if (!isLoadingSearchedUsers && !hasInitiallyLoaded) {
      setHasInitiallyLoaded(true)
    }
  }, [isLoadingSearchedUsers, hasInitiallyLoaded])

  // In mini app, searchedUsers already contains filtered results from API
  // No need for additional client-side filtering like in React Native
  const filteredContacts = searchedUsers

  const handleContactPress = (contact: UsersAndContacts) => {
    // Check if user can transfer to non-users
    if (contact.username || isEnabledTransferToNonUsers) {
      onContactSelected(contact)
    } else {
      // For non-users when transfer is not enabled, could show SMS option
      // For now, just call the handler
      onContactSelected(contact)
    }
  }

  // Show loading when searching - улучшенный дизайн
  if (search && search.length > 0 && isLoadingSearchedUsers) {
    if (showInlineResults) {
      // Показываем скелетоны для inline режима (как в RN версии)
      return (
        <BaseView className={className}>
          <SkeletonListTemplate
            sizeList={3}
            itemHeight={72}
            itemRadius={34}
            paddingList={0}
            itemWidth="100%"
            showItemBackground={true}
            itemMarginBottom={true}
            containerGap={6}
          />
        </BaseView>
      )
    }

    return (
      <BaseView
        className={`flex-1 justify-center items-center p-8 ${className}`}
      >
        <BaseView className="animate-pulse">
          <LoadingSpinner size="large" />
        </BaseView>
        <BaseText
          className="mt-6 text-center"
          style={{
            color: 'var(--color-label-description)',
            fontSize: '16px',
            fontWeight: '500',
          }}
        >
          Searching users...
        </BaseText>
        <BaseText
          className="mt-2 text-center"
          style={{
            color: 'var(--color-label-description)',
            fontSize: '14px',
            opacity: 0.7,
          }}
        >
          Finding &quot;{search}&quot;
        </BaseText>
      </BaseView>
    )
  }

  // Show "no results" when search completed but no results - улучшенный дизайн
  if (
    search &&
    search.length > 0 &&
    !isLoadingSearchedUsers &&
    hasInitiallyLoaded &&
    filteredContacts.length === 0
  ) {
    if (showInlineResults) {
      // Компактное сообщение для inline режима
      return (
        <BaseView className={`px-4 py-6 ${className}`}>
          <BaseText
            style={{
              color: '#9FA3A4',
              fontSize: '16px',
              textAlign: 'center',
              lineHeight: '24px',
              margin: 0,
            }}
          >
            No results found for the search term &quot;{search}&quot;. Please
            try a different keyword.
          </BaseText>
        </BaseView>
      )
    }

    return (
      <BaseView
        className={`flex-1 justify-center items-center p-8 ${className}`}
      >
        <BaseView
          className="mb-6 p-4 rounded-full"
          style={{
            backgroundColor: 'var(--color-background-secondaryGray)',
          }}
        >
          <svg
            className="w-12 h-12"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            style={{ color: 'var(--color-icon-disabledInputIcon)' }}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </BaseView>
        <BaseText
          className="text-center"
          style={{
            color: 'var(--color-label-textTextInput)',
            fontSize: '18px',
            fontWeight: '600',
            marginBottom: '8px',
          }}
        >
          No users found
        </BaseText>
        <BaseText
          className="text-center max-w-xs"
          style={{
            color: 'var(--color-label-description)',
            fontSize: '14px',
            lineHeight: '20px',
          }}
        >
          Try searching with a different name, username, or phone number
        </BaseText>
      </BaseView>
    )
  }

  // Show default state when no search - убираем, показываем избранные
  if (!search || search.length === 0) {
    return null // Возвращаем null, избранные показываются в родительском компоненте
  }

  return (
    <BaseView className={`flex-1 ${className}`}>
      {/* Refresh indicator - показываем только если не inline режим */}
      {!showInlineResults && isRefreshingUsersContacts && (
        <BaseView
          data-component="contacts-list-refresh-indicator"
          className="p-3 mx-3 mb-2 rounded-2xl"
          style={{
            backgroundColor: 'var(--color-background-profitFlag)',
            opacity: 0.1,
          }}
        >
          <BaseView className="flex-row items-center justify-center">
            <LoadingSpinner size="small" className="mr-3" />
            {/* <BaseText
              style={{
                color: 'var(--color-label-flagProfit)',
                fontSize: '14px',
                fontWeight: '500',
              }}
            >
              Updating contacts...
            </BaseText> */}
          </BaseView>
        </BaseView>
      )}

      {/* List Header Component - показываем только если не inline режим */}
      {!showInlineResults && ListHeaderComponent && (
        <BaseView>{ListHeaderComponent}</BaseView>
      )}

      {/* Contacts list */}
      <BaseView className="flex-1 gap-1">
        {filteredContacts.map((contact, index) => (
          <ContactItem
            key={`${contact.id}-${index}`}
            contact={contact}
            onPress={handleContactPress}
            showGiftMessage={Boolean(giftMessage)}
            giftMessage={giftMessage}
            isMultiSelectMode={isMultiSelectMode}
            isSelected={
              isMultiSelectMode && isContactSelected
                ? isContactSelected(contact.id)
                : false
            }
          />
        ))}
      </BaseView>

      {/* Pull to refresh hint - показываем только если не inline режим */}
      {!showInlineResults &&
        !isLoadingSearchedUsers &&
        filteredContacts.length > 0 && (
          <BaseView data-component="contacts-list-refresh-hint" className="p-4">
            <BaseView className="flex-row items-center justify-center">
              <button
                onClick={onRefreshUsersContacts}
                className="px-4 py-2 rounded-full transition-all duration-200 hover:bg-opacity-80"
                style={{
                  backgroundColor: 'var(--color-background-secondaryGray)',
                  fontSize: '14px',
                  fontWeight: '500',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  color: '#232425',
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M17.65 6.35C16.2 4.9 14.21 4 12 4C7.58 4 4.01 7.58 4.01 12C4.01 16.42 7.58 20 12 20C15.73 20 18.84 17.45 19.73 14H17.65C16.83 16.33 14.61 18 12 18C8.69 18 6 15.31 6 12C6 8.69 8.69 6 12 6C13.66 6 15.14 6.69 16.22 7.78L13 11H20V4L17.65 6.35Z"
                    fill="#232425"
                  />
                </svg>{' '}
                Click to refresh
              </button>
            </BaseView>
          </BaseView>
        )}
    </BaseView>
  )
}

export default ContactsList
